import {
  ContentContainer,
  Icon,
} from "@twinkltech/curriculum-architect-component-library";
import { Button, IconActionsAdd, Text } from "@twinkltech/web-ui";
import { Trans, useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { generatePath, Link } from "react-router-dom";
import remarkGfm from "remark-gfm";

import { GenericError } from "@/components/GenericError/GenericError";
import {
  ResourceList,
  ResourceListItem,
  ResourceListItemLink,
} from "@/components/ResourceLinkList/ResourceLinkList";
import { StyledTag } from "@/components/StyledTag/StyledTag";
import { UnitHeader } from "@/components/UnitHeader/UnitHeader";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import { UnitInfo } from "@/utils/getUnitFromCell/getUnitFromCell";
import { ResourceHelper } from "@/utils/ResourceHelper/ResourceHelper";

import { paths } from "@/paths";

import { captureAppError } from "@/utils/monitoring/sentry";
import { LEARNING_SEQUENCE_NS } from "./LearningSequence.i18n";

import type {
  ApiErrorResponse,
  Curriculum,
  LessonPackResource,
  LinkedResource,
  UnitPlan as UnitPlanType,
} from "@twinkltech/curriculum-architect-domain-hooks";

type UnitPlanProps = {
  unitInfo?: UnitInfo;
  curriculum?: Curriculum | null;
  subject?: UnitPlanType["subject"];
  error?: ApiErrorResponse | null;
  isLoading: boolean;
  title?: string;
  description?: string;
  type: UnitPlanType["type"] | null;
  subjectId: string;
  lessonPacks: LessonPackResource[];
  linkedResources: LinkedResource[];
  unitId: string;
  unitPackLink: string;
  keyArt?: string;
};

export const LearningSequence = ({
  curriculum,
  description,
  error,
  isLoading,
  keyArt,
  lessonPacks,
  linkedResources,
  subject,
  title,
  unitId,
  unitInfo,
}: UnitPlanProps) => {
  const { t } = useTranslation(LEARNING_SEQUENCE_NS);

  if (isLoading) {
    return <LoadingPage />;
  } else if (error || !curriculum || !subject) {
    captureAppError(error || new Error("Unknown error loading learning sequence"), {
      action: "load",
      area: "learning-sequence",
    });
    return <GenericError />;
  }

  const hasAdaptationNotes = Boolean(unitInfo?.cell?.adaptationNote);

  return (
    <ContentContainer>
      <UnitHeader
        curriculum={curriculum}
        description={description}
        keyArt={keyArt}
        subject={subject}
        title={title}
        unitId={unitId}
        unitInfo={unitInfo}
      />
      {unitInfo?.cell ? (
        <div className="bg-neutral-100 py-8 px-10 mb-6 rounded-md">
          <Text
            as="h2"
            className="uppercase text-xs text-neutral-700"
            variant="sm-medium"
          >
            Adaptation Notes
          </Text>
          <div className="flex justify-between mt-4">
            {hasAdaptationNotes ? (
              <div className="prose-richtext">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {unitInfo?.cell?.adaptationNote}
                </Markdown>
              </div>
            ) : (
              <div>
                <Text className="text-neutral-700" variant="base-normal">
                  No notes added.
                </Text>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Button
                as={Link}
                className="gap-2 text-blue-850"
                size="sm"
                to={generatePath(paths.SUBJECT_PLAN_EDIT_PATH, {
                  curriculum_id: curriculum.id,
                  subject_id: subject.subjectId,
                })}
                variant="outlined"
              >
                {hasAdaptationNotes ? (
                  <>
                    <Icon icon="Edit" />
                    Edit adaptation notes
                  </>
                ) : (
                  <>
                    <IconActionsAdd />
                    Add adaptation notes
                  </>
                )}
              </Button>
              <Text className="text-neutral-750 text-xs" variant="sm-medium">
                Via dropdown menu for this placed unit
              </Text>
            </div>
          </div>
        </div>
      ) : null}
      {lessonPacks.map((lessonPack) => (
        <div
          key={lessonPack.resourceId}
          aria-labelledby={`lesson-pack-id-${lessonPack.resourceId}`}
          className="mb-10"
          role="region"
        >
          <div className="flex gap-800 bg-neutral-100 p-600 rounded-xl">
            <div className="max-w-[432px] flex flex-col justify-start w-full gap-300">
              <div className="flex items-end w-full">
                <div className="flex flex-auto flex-col gap-150">
                  <Text
                    className="text-neutral-700 font-medium uppercase"
                    variant="sm-normal"
                  >
                    {t("lesson-info")}
                  </Text>
                  <img
                    alt={lessonPack.title}
                    className="rounded"
                    src={lessonPack.previewImageUrl}
                  />
                  <Text
                    id={`lesson-pack-id-${lessonPack.resourceId}`}
                    variant="xl-medium"
                  >
                    {lessonPack.title}
                  </Text>
                </div>
              </div>
              <div>
                {!!lessonPack.threads && lessonPack.threads?.length > 0 && (
                  <div className="flex gap-100 mb-300 flex-wrap">
                    {/* Temporarily removed until we get the data */}
                    {lessonPack.threads?.map((thread) => (
                      <StyledTag key={thread} size="sm" variant="default-alt">
                        {ResourceHelper.getThreadLabel(thread)}
                      </StyledTag>
                    ))}
                  </div>
                )}
                <Text variant="base-normal">{lessonPack.description}</Text>
              </div>
            </div>
            <div className="w-full flex flex-col gap-2 max-w-[680px]">
              <Text
                className="text-neutral-700 font-medium uppercase"
                id="unit-content-id"
                variant="sm-normal"
              >
                {t("list-title")}
              </Text>
              <ResourceList>
                {lessonPack.canonicalUrl ? (
                  <ResourceListItem>
                    <ResourceListItemLink
                      aria-labelledby="unit-content-id"
                      className="bg-white hover:bg-neutral-150"
                      rel="noopener noreferrer"
                      target="_blank"
                      to={`${paths.external.TWINKL_URL}${lessonPack.canonicalUrl}`}
                      variant="learning-sequence"
                    >
                      <span>
                        <Trans
                          components={{
                            span: <span className="font-medium" />,
                          }}
                          i18nKey="unit-pack-link-label"
                          id="unit-content-id"
                          ns={LEARNING_SEQUENCE_NS}
                          values={{
                            lessonTitle: lessonPack.title,
                          }}
                        />
                      </span>
                    </ResourceListItemLink>
                  </ResourceListItem>
                ) : null}
                {lessonPack.resources.map((resource) => {
                  const targetResource = linkedResources.find(
                    (res) => res.resourceId === resource
                  );
                  if (!targetResource) return null;

                  const linkVariant = targetResource.isPinned
                    ? "learning-sequence"
                    : "additional-resource";

                  return (
                    <ResourceListItem key={targetResource.resourceId}>
                      <ResourceListItemLink
                        aria-label={targetResource.title}
                        className="bg-white hover:bg-neutral-150"
                        rel="noopener noreferrer"
                        target="_blank"
                        to={`${paths.external.TWINKL_URL}${targetResource.canonicalUrl}`}
                        variant={linkVariant}
                      >
                        <span>
                          <span className="font-medium">
                            {ResourceHelper.getTypeLabel({
                              slug: targetResource.type,
                            })}
                          </span>
                          : {targetResource.title}
                        </span>
                      </ResourceListItemLink>
                    </ResourceListItem>
                  );
                })}
              </ResourceList>
            </div>
          </div>
        </div>
      ))}
    </ContentContainer>
  );
};
